import {
  CheckBox,
  DateRangePicker,
  TextArea,
  TextInput
} from "@dealer-center/core-components";
import "./GeneralDetailsPanel.scss";
import { Locale } from "@dealer-center/ddc-core-utils";
import {
  incentiveDate,
  newDateTimeOffset,
  updateContent
} from "../utils/utils";
import type { IncentivesRecord } from "@dealer-center/ddc-pages-incentives-manager-interface";
import {
  combineValidations,
  dateRangeValidation,
  prohibitedContentValidation,
  requiredValidation
} from "../utils/validation";
import type { EditIncentivesDisableFlags } from "@dealer-center/ddc-pages-incentives-manager-interface";
import { useEffect, useMemo, useRef } from "react";

export const GeneralDetailsPanel = ({
  changeError,
  languageCode,
  disableFlags,
  incentive,
  setIncentive
}: {
  changeError: (id: string, status: boolean) => void;
  languageCode: string;
  disableFlags: EditIncentivesDisableFlags;
  incentive: IncentivesRecord;
  setIncentive: (
    updater: (prevState: IncentivesRecord) => IncentivesRecord
  ) => void;
}) => {
  const dateValidation = dateRangeValidation(
    incentive.effectiveDate === -1
      ? -1
      : incentiveDate(incentive.effectiveDate),
    incentive.expirationDate === -1
      ? -1
      : incentiveDate(incentive.expirationDate)
  );

  const hasStartDateError = dateValidation.startDateError;
  const hasEndDateError = dateValidation.endDateError;
  const hasRangeError = dateValidation.rangeError;

  const bothDatesInPast = (() => {
    if (incentive.effectiveDate === -1 || incentive.expirationDate === -1) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = new Date(incentiveDate(incentive.effectiveDate));
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(incentiveDate(incentive.expirationDate));
    endDate.setHours(0, 0, 0, 0);

    return startDate < today && endDate < today;
  })();

  const dateRange = useMemo(
    () => ({
      from:
        incentive.effectiveDate !== -1
          ? new Date(incentiveDate(incentive.effectiveDate))
          : undefined,
      to:
        incentive.expirationDate !== -1
          ? new Date(incentiveDate(incentive.expirationDate))
          : undefined
    }),
    [incentive.effectiveDate, incentive.expirationDate]
  );

  const prevErrors = useRef({
    start: false,
    end: false,
    range: false
  });

  useEffect(() => {
    if (hasStartDateError !== prevErrors.current.start) {
      changeError("start-date-picker", hasStartDateError);
      prevErrors.current.start = hasStartDateError;
    }
    if (hasEndDateError !== prevErrors.current.end) {
      changeError("end-date-picker", hasEndDateError);
      prevErrors.current.end = hasEndDateError;
    }
    if (hasRangeError !== prevErrors.current.range) {
      changeError("date-range", hasRangeError);
      prevErrors.current.range = hasRangeError;
    }
  }, [hasStartDateError, hasEndDateError, hasRangeError, changeError]);

  return (
    <div className="general-details-panel">
      <div className="top-panel">
        <TextInput
          className={disableFlags.readOnly ? "" : "required"}
          getError={combineValidations(
            requiredValidation(
              "title",
              incentive.content.title || "",
              changeError
            ),
            prohibitedContentValidation(
              "title-prohibited",
              incentive.content.title || "",
              changeError
            )
          )}
          label={String(Locale.getText("OFFER"))}
          name={"offer"}
          readOnly={disableFlags.readOnly}
          value={incentive.content.title}
          onChange={event => {
            setIncentive(prevState => {
              return updateContent(
                prevState,
                "title",
                event.target.value,
                languageCode
              );
            });
          }}
        />
        <TextInput
          className={
            disableFlags.readOnly && !disableFlags.publisherMode
              ? ""
              : "required"
          }
          getError={combineValidations(
            requiredValidation(
              "shortTitle",
              incentive.extractedShortTitle || "",
              changeError
            ),
            prohibitedContentValidation(
              "shortTitle-prohibited",
              incentive.extractedShortTitle || "",
              changeError
            )
          )}
          label={String(Locale.getText("SHORT_TITLE"))}
          name={"short-title"}
          readOnly={disableFlags.readOnly && !disableFlags.publisherMode}
          value={incentive.extractedShortTitle}
          onChange={event => {
            setIncentive(prevState => {
              return {
                ...prevState,
                extractedShortTitle: event.target.value
              };
            });
          }}
        />
      </div>
      <div className="check-panel">
        <CheckBox
          checked={incentive.conditional}
          disabled={disableFlags.readOnly}
          label={String(Locale.getText("OFFER_CONDITIONAL"))}
          name={"offer-conditional"}
          value={"offer-conditional"}
          onChange={event => {
            setIncentive(prevState => {
              return {
                ...prevState,
                conditional: event.target?.value?.checked
              };
            });
          }}
        />
        <CheckBox
          checked={incentive.featured}
          disabled={disableFlags.readOnly}
          label={String(Locale.getText("FEATURED"))}
          name={"featured"}
          value={"featured"}
          onChange={event => {
            setIncentive(prevState => {
              return {
                ...prevState,
                featured: event.target?.value?.checked
              };
            });
          }}
        />
        <CheckBox
          checked={incentive.accountMap?.dealerStackOption}
          disabled={!disableFlags.dealerStackOptionEnabled}
          // TODO: ref: https://ghe.coxautoinc.com/DDC-Dont-Panic/control-center-ui/blob/74b196cdcfa0dde3d5ec05c065a9df346f401356/plugins/globalvehiclemanager/htdocs/globalvehiclemanager/js/ext-add-incentive-dialog.js#L118
          label={String(Locale.getText("STACK_INCENTIVE"))}
          name={"stack-incentive"}
          value={"stack-incentive"}
          onChange={event => {
            setIncentive(prevState => {
              return {
                ...prevState,
                accountMap: {
                  ...prevState.accountMap,
                  dealerStackOption: event.target?.value?.checked
                }
              };
            });
          }}
        />
      </div>
      <div className="bottom-panel">
        <DateRangePicker
          dateRange={dateRange}
          /*           defaultDateRange={
            incentive.effectiveDate !== -1 && incentive.expirationDate !== -1
              ? {
                  from: new Date(incentiveDate(incentive.effectiveDate)),
                  to: new Date(incentiveDate(incentive.expirationDate))
                }
              : undefined
          }
 */ errorMessage={
            hasRangeError ||
            (hasStartDateError && hasEndDateError) ||
            bothDatesInPast
              ? String(Locale.getText("DATE_RANGE_ERROR"))
              : hasStartDateError
                ? String(Locale.getText("START_DATE_ERROR"))
                : String(Locale.getText("END_DATE_ERROR"))
          }
          hasError={hasRangeError || hasStartDateError || hasEndDateError}
          id={disableFlags.readOnly ? "date-picker" : "date-picker-required"}
          label={String(Locale.getText("DATE"))}
          readOnly={disableFlags.readOnly}
          onChange={event => {
            setIncentive(prevState => {
              let prevExpirationDate = prevState.expirationDate;
              if (
                event.target.value.from?.dateValue &&
                !event.target.value.to?.dateValue
              ) {
                prevExpirationDate = -1;
              }
              return {
                ...prevState,
                effectiveDate: event.target.value.from?.dateValue
                  ? new Date(
                      newDateTimeOffset(event.target.value.from?.dateValue)
                    ).getTime()
                  : prevState.effectiveDate,
                expirationDate: event.target.value.to?.dateValue
                  ? new Date(
                      newDateTimeOffset(event.target.value.to?.dateValue)
                    ).getTime()
                  : prevExpirationDate
              };
            });
          }}
          onClearCallback={() => {
            setIncentive(prevState => {
              return {
                ...prevState,
                effectiveDate: -1,
                expirationDate: -1
              };
            });
          }}
        />
        <TextArea
          getError={prohibitedContentValidation(
            "offerDetails-prohibited",
            incentive?.content?.offerDetails || "",
            changeError
          )}
          label={String(Locale.getText("OFFER_DETAILS"))}
          name="offer-details"
          readOnly={disableFlags.readOnly}
          rows={3}
          value={incentive?.content?.offerDetails || ""}
          onChange={event => {
            setIncentive(prevState =>
              updateContent(
                prevState,
                "offerDetails",
                event.target.value,
                languageCode
              )
            );
          }}
        />
        <TextArea
          getError={prohibitedContentValidation(
            "content-prohibited",
            incentive?.content?.content || "",
            changeError
          )}
          label={String(Locale.getText("DESCRIPTION"))}
          name="Description"
          readOnly={disableFlags.readOnly}
          rows={3}
          value={incentive?.content?.content || ""}
          onChange={event => {
            setIncentive(prevState =>
              updateContent(
                prevState,
                "content",
                event.target.value,
                languageCode
              )
            );
          }}
        />
        <TextArea
          className={disableFlags.readOnly ? "" : "required"}
          getError={combineValidations(
            requiredValidation(
              "disclaimer",
              incentive.content.disclaimer || "",
              changeError
            ),
            prohibitedContentValidation(
              "disclaimer-prohibited",
              incentive.content.disclaimer || "",
              changeError
            )
          )}
          label={String(Locale.getText("DISCLAIMER"))}
          name="Disclaimer"
          readOnly={disableFlags.readOnly}
          rows={3}
          value={incentive?.content?.disclaimer || ""}
          onChange={event => {
            setIncentive(prevState =>
              updateContent(
                prevState,
                "disclaimer",
                event.target.value,
                languageCode
              )
            );
          }}
        />
      </div>
    </div>
  );
};
